import styles from './TimeCardPreview.module.scss';

import { useEventCallback } from '@mui/material/utils';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as PauseIcon } from '@work4all/assets/icons/statistics/pause_circle_filled.svg';
import { ReactComponent as PlayIcon } from '@work4all/assets/icons/statistics/play_circle_filled.svg';
import { ReactComponent as StopIcon } from '@work4all/assets/icons/statistics/stop-circle.svg';

import { TimeInputPicker } from '@work4all/components/lib/input/time-input-picker';

import { useUser } from '@work4all/data';

import { UserPresenceTimeInfo } from '@work4all/models/lib/Classes/UserPresenceTimeInfo.entity';
import { TimeStampKind } from '@work4all/models/lib/Enums/TimeStampKind.enum';

import { useTimeCardDetails } from './hooks/useTimeCardDetails';
import { useTimeEntryMutation } from './hooks/useTimeEntryMutation';
import { TimeCardPreviewTimeStamp } from './TimeCardPreviewTimestamp';
import { isValidPause } from './utils';

interface Props {
  block: UserPresenceTimeInfo;
  separator: boolean;
}

type OnPauseChangeProps = {
  start: string | undefined;
  end: string | undefined;
  updateId: number;
  updateFiled: 'start' | 'end';
};

export const TimeCardPreviewBlock: React.FC<Props> = (props: Props) => {
  const { block, separator } = props;
  const { t } = useTranslation();
  const { disabledPause, disabled, isLast } = useTimeCardDetails();

  const { update, remove, onBlur } = useTimeEntryMutation();

  const user = useUser();
  const { enqueueSnackbar } = useSnackbar();
  const showMinimumPauseError = useEventCallback(() => {
    enqueueSnackbar(
      t('ERROR.MINIMUM_PAUSE_TIME', {
        value: user.workTimeTrackingMinBreakTime,
      }),
      {
        variant: 'error',
        autoHideDuration: 5000,
      }
    );
  });

  const onPauseChange = useEventCallback(
    ({ start, end, updateId, updateFiled }: OnPauseChangeProps) => {
      const isValid = isValidPause({
        start,
        end,
        minimumPauseAmount: user.workTimeTrackingMinBreakTime,
      });

      if (!isValid) {
        showMinimumPauseError();
        return;
      }

      update(updateId, updateFiled === 'start' ? start : end);
    }
  );

  if (!block.items?.length) return <></>;
  const array = block.items.map((stamp, id) => {
    if (stamp.kind === TimeStampKind.MANUELL)
      return (
        <TimeCardPreviewTimeStamp
          {...stamp}
          key={`stamp_${stamp.timestamp}`}
          icon={<PlayIcon />}
          disabled={disabled}
          label={t('INPUTS.START')}
          onClear={
            isLast(stamp.id)
              ? () => {
                  remove(stamp.id);
                }
              : undefined
          }
        />
      );
    if (stamp.kind === TimeStampKind.TAG_BEENDET)
      return (
        <TimeCardPreviewTimeStamp
          {...stamp}
          key={`stamp_${stamp.timestamp}`}
          icon={<StopIcon />}
          disabled={disabled}
          label={t('INPUTS.END')}
          onClear={
            isLast(stamp.id)
              ? () => {
                  remove(stamp.id);
                }
              : undefined
          }
        />
      );

    if (stamp.kind === TimeStampKind.PAUSENBEGINN) {
      const endTimeStamp = block.items[id + 1];
      return (
        <div
          key={`stamp_${stamp.timestamp}`}
          className={styles['double-time-entry']}
        >
          <div className={styles['time-preview__icon']}>
            <PauseIcon />
          </div>

          <div
            className={clsx(
              styles['time-preview__item'],
              styles['time-preview__row']
            )}
          >
            <TimeInputPicker
              onChange={(e) => {
                onPauseChange({
                  start: e.currentTarget?.value,
                  end: endTimeStamp?.timestamp,
                  updateId: stamp.id,
                  updateFiled: 'start',
                });
              }}
              onDestroyPopover={onBlur}
              disabled={disabledPause}
              className={styles['double-time-entry--left']}
              rootClassName={styles['double-time-entry__item']}
              label={t('INPUTS.PAUSE')}
              defaultValue={DateTime.fromISO(stamp.timestamp)}
              onClear={
                isLast(stamp.id)
                  ? () => {
                      remove(stamp.id);
                    }
                  : undefined
              }
            />
            {endTimeStamp && (
              <TimeInputPicker
                onChange={(e) => {
                  onPauseChange({
                    start: stamp?.timestamp,
                    end: e.currentTarget?.value,
                    updateId: endTimeStamp.id,
                    updateFiled: 'end',
                  });
                }}
                onDestroyPopover={onBlur}
                disabled={disabledPause}
                label={t('INPUTS.END')}
                className={styles['double-time-entry--right']}
                rootClassName={styles['double-time-entry__item']}
                defaultValue={DateTime.fromISO(endTimeStamp.timestamp)}
                onClear={
                  isLast(endTimeStamp.id)
                    ? () => {
                        remove(endTimeStamp.id);
                      }
                    : undefined
                }
              />
            )}
          </div>
        </div>
      );
    }
  });

  return (
    <>
      <div className={styles['time-preview__block']}>{array}</div>
      {separator ? (
        <div
          key={`${block.start}_separator`}
          className={styles['time-preview__separator']}
        />
      ) : undefined}
    </>
  );
};
